import React, { useEffect, useMemo, useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  Filler
} from "chart.js";
import * as masterData from "../../../Master Data/masterData.js";
import { Bar } from "react-chartjs-2";
import Colors from "../../../assets/Variables/ColorsVariables.js";
import * as TextVariables from "../../../assets/Variables/TextVariables.js";
import { useDynamicStyles } from "./Styles.js";
import SlimSelector from "../Selectors/SlimSelector.js";
import { useTranslation } from "react-i18next";
import signalService from "../../../services/signalProvider";
import LoadingSpinner from "../../Generals/Loaders/SpinLoader";
import { useBreakpoint } from "../../../config/BreakpointContext.js";
// Registrar componentes necesarios de Chart.js
ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function ProfitabilityChart({
  GraphicType,
  dataChart,
  strategyData,
  yLabel = "No defined",
  adminSignalLogin,
  selectedYear,
  setSelectedYear,
}) {

  const { isXs, isMd } = useBreakpoint();

  const styles = useDynamicStyles();
  const [t] = useTranslation("global");
  const [period, setPeriod] = useState("annual");
  const [monthMaxLimit, setMonthMax] = useState([]);
  const [monthMinLimit, setMonthMin] = useState([]);
  const [month, setMonth] = useState(1);
  const [quarter, setQuarter] = useState(1);
  const [labels, setLabels] = useState([]);
  const [values, setValues] = useState([]);
  const [profit, setProfit] = useState([]);
  const [section, setSection] = useState("");
  const [semiannual, setSemiannual] = useState(1);
  const [loading, setLoading] = useState(false);
  // Bandera para asegurar que el valor del API se aplique SOLO UNA VEZ
  const hasAppliedApiMin = useRef(false);
  // (Opcional) si quieres bloquear que se aplique después de que el usuario cambie algo:
  const userTouched = useRef(false);

  useEffect(() => {
    const fetchData = async () => {

      setLoading(true);
      try {
        if (period.toLowerCase().replace(/\s+/g, '') === "monthly") {
          setSection(month)
        } else if (period.toLowerCase().replace(/\s+/g, '') === "quarterly") {
          setSection(quarter)
        } else if (period.toLowerCase().replace(/\s+/g, '') === "semiannual") {
          setSection(semiannual)
        }

        // VALIDACIÓN: Seleccionamos el servicio según el contexto
        const response = adminSignalLogin
          ? await signalService.getSignalDataChartAdmin(
            adminSignalLogin, // Usamos el login que viene de las props
            period,
            section,
            selectedYear
          )
          : await signalService.getSignalDataChart(
            strategyData.signal_login,
            period,
            section,
            selectedYear
          );


        const nextLabels = period.toLowerCase().replace(/\s+/g, '') === "quarterly"
          || period.toLowerCase().replace(/\s+/g, '') === "semiannual" || period.toLowerCase().replace(/\s+/g, '') === "annual"
          ? response.data.data.data.map(item => {
            const m = masterData.monthsSimplyValues.find((m) => m.value === item.x_axis);
            return m ? m.label : null;
          })
          : response.data.data.data.map(item => item.x_axis);

        const nextValues = response.data.data.data.map(item => item.profitability);
        const maxMonth = response.data.data.total_months_in_year;
        const minMonth = response.data.data.start_month_in_year;

        setMonthMax(maxMonth);
        setMonthMin(minMonth);

        if (minMonth != null && !hasAppliedApiMin.current && !userTouched.current) {
          setMonth(minMonth);
          hasAppliedApiMin.current = true;
        }

        const profitValue = response.data.data.profitability_percentage;
        setLabels(nextLabels);
        setValues(nextValues);
        setProfit(profitValue);

      } catch (error) {
        console.error("Error obteniendo datos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

  }, [period, selectedYear, month, quarter, section, semiannual, adminSignalLogin]);

  const data = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: "Profitability",
          data: values,
          borderWidth: 1,
          //borderColor: "rgba(255, 0, 0, 0.6)",
          backgroundColor: (context) => {
            const value = context.raw; // valor de la barra
            return value > 0 ? "rgba(34, 197, 94, 0.8)" : "rgba(239,68,68,0.8)";
            // verde-500 o rojo-500 en Tailwind
          },
        },
      ],
    }),
    [labels, values]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true },
        title: { display: false },
      },
      scales: {
        y: {
          beginAtZero: false,
          title: { display: true, text: "Profitability" },
        },
      },
    }),
    []
  );
  const OptionsLine = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    stacked: false,
    plugins: {
      legend: {
        display: false, // Oculta la leyenda predeterminada
      },
      title: {
        display: true,
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: false,
          text: "Meses",
        },
        grid: {
          display: false, // Oculta las líneas de la cuadrícula horizontal
        },
        ticks: {
          font: {
            family: TextVariables.Overline.fontFamily,
            size: TextVariables.Overline.size,
            weight: TextVariables.Overline.weight,
          },
          color: Colors.Secondary_600,
        },
      },
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: {
          display: true,
          text: yLabel,
          font: {
            family: TextVariables.Overline.fontFamily,
            size: TextVariables.Overline.size,
            weight: TextVariables.Overline.weight,
          },
        },
        beginAtZero: true,

        ticks: {
          callback: function (value) {
            return masterData.formatYAxis(value);
          },
          font: {
            family: TextVariables.Overline.fontFamily,
            size: TextVariables.Overline.size,
            weight: TextVariables.Overline.weight,
          },
          color: Colors.Secondary_600,
        },
      },
    },
  };

  const handleYearSelector = (e) => {
    setSelectedYear(e.target.value);
  };

  return (
    <Box >
      <Box sx={isMd ? styles.comboBoxChartJS : isXs ? styles.comboBoxChartJSmb : {}}  >
        <Box sx={styles.quoteChartJS}>
          <Typography
            sx={{
              fontFamily: TextVariables.Body_4_Bold,
              lineHeight: 2,
              fontSize: "1.3rem"
            }}
          >
            Yield: {moreThanTwoDecimals(profit)}  %
          </Typography>
        </Box>
        <Box sx={isMd ? styles.comboChild : isXs ? styles.comboChildmb : {}}>

          {loading ? <LoadingSpinner /> : <></>}

          <Box  >
            <SlimSelector title={t("PeriodTime")} value={period} handleChange={(e) => setPeriod(e.target.value)} options={masterData.periodTime} />
          </Box>

          {period.toLowerCase().replace(/\s+/g, '') !== 'full' && (
            <Box >
              <SlimSelector title={t("Year")} value={selectedYear} handleChange={handleYearSelector} options={masterData.years} />
            </Box>)}


          {period.toLowerCase().replace(/\s+/g, '') === 'monthly' && (
            <Box >
              <SlimSelector title={t("Month")} value={month} handleChange={(e) => setMonth(e.target.value)}
                options={masterData.monthsSimplyValue.slice(monthMinLimit - 1, monthMaxLimit)}

              />
            </Box>
          )}

          {period.toLowerCase().replace(/\s+/g, '') === 'quarterly' && (
            <Box >
              <SlimSelector title={t("Quarter")} value={quarter} handleChange={(e) => setQuarter(e.target.value)}
                options={masterData.quarter}
              />
            </Box>
          )}

          {period.toLowerCase().replace(/\s+/g, '') === 'semiannual' && (
            <Box >
              <SlimSelector title={t("Quarter")} value={semiannual} handleChange={(e) => setSemiannual(e.target.value)}
                options={masterData.semiannual}
              />
            </Box>
          )}

        </Box>
      </Box>


      <Box sx={{
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        maxHeight: "100%"
      }}>
        <Bar options={OptionsLine} data={data} />
      </Box>


    </Box>

  );

  function moreThanTwoDecimals(num) {
    if (num === null || num === undefined || isNaN(num)) return 0;
    if (Number(num) === 0) return 0;

    const numStr = num.toString();
    const parts = numStr.split(".");

    // Si no hay decimales, devolvemos el número tal cual
    if (parts.length < 2) return num;

    // Si tiene más de 3 decimales, redondeamos a 2
    return parts.length > 3 ? Number(num).toFixed(2) : num;
  }

}

